import type { SidebarNavItem } from "../config/navigation";

/**
 * Props for the vertical navigation used inside the admin shell sidebar.
 */
export interface SidebarNavProps {
  items: SidebarNavItem[];
  activeHref?: string;
  onNavigate?: (href: string) => void;
  className?: string;
}

/**
 * Renders navigation links with support for nested items and active highlighting.
 */
export function SidebarNav({ items, activeHref, onNavigate, className }: SidebarNavProps): JSX.Element {
  const navClassName = ["flex flex-col gap-1 text-sm text-foreground", className].filter(Boolean).join(" ");

  const renderItems = (navItems: SidebarNavItem[], depth = 0): JSX.Element[] => {
    return navItems.map((item) => {
      const isActive = activeHref === item.href;
      const triggerClasses = [
        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        depth > 0 ? "pl-6 text-muted-foreground hover:text-foreground" : "font-medium text-foreground",
        isActive ? "bg-muted text-foreground" : "hover:bg-muted/70",
      ].join(" ");

      const labelClasses = "truncate uppercase tracking-wide font-semibold";

      const content = (
        <>
          {item.icon ? (
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{item.icon}</span>
          ) : null}
          <span className={labelClasses}>{item.label}</span>
        </>
      );

      const handleNavigate = (): void => {
        if (onNavigate) {
          onNavigate(item.href);
        }
      };

      const trigger = onNavigate ? (
        <button type="button" className={triggerClasses} onClick={handleNavigate}>
          {content}
        </button>
      ) : (
        <a href={item.href} className={triggerClasses}>
          {content}
        </a>
      );

      return (
        <li key={item.id}>
          {trigger}
          {item.children && item.children.length > 0 ? (
            <ul className="mt-1 space-y-1">{renderItems(item.children, depth + 1)}</ul>
          ) : null}
        </li>
      );
    });
  };

  return (
    <nav className={navClassName}>
      <ul className="space-y-1">{renderItems(items)}</ul>
    </nav>
  );
}
